"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SalesOrder = exports.TSalesOrderStatus = void 0;
const class_validator_1 = require("class-validator");
const moment = require("moment-timezone");
const RetailLocation_1 = require("./RetailLocation");
const RetailCustomer_1 = require("./RetailCustomer");
const SalesOrderRefund_1 = require("./SalesOrderRefund");
const TSalesOrderType_1 = require("../types/TSalesOrderType");
const cloneModelObject_1 = require("../../../../apis/spg/util/cloneModelObject");
const errorTypes_1 = require("../../../../apis/spg/helpers/errorTypes");
const salesOrderItemUnserializeFactory_1 = require("../helpers/salesOrderItemUnserializeFactory");
const labels_1 = require("../types/labels");
const TSalesOrderItemType_1 = require("../types/TSalesOrderItemType");
const getUniqSortedCategories_1 = require("../helpers/getUniqSortedCategories");
const printing_1 = require("../types/printing");
const currentUnixTimestamp_1 = require("../../../../apis/spg/util/currentUnixTimestamp");
const getOrderMainItemImage_1 = require("../helpers/getOrderMainItemImage");
const constants_1 = require("../../../../helpers/constants");
const time_1 = require("../../../../helpers/time");
const isDeliverySalesOrderItem_1 = require("../helpers/isDeliverySalesOrderItem");
const isServiceSalesOrderItem_1 = require("../helpers/isServiceSalesOrderItem");
const delivery_1 = require("../types/delivery");
// sales order statuses
var TSalesOrderStatus;
(function (TSalesOrderStatus) {
    TSalesOrderStatus["NotSubmitted"] = "notSubmitted";
    TSalesOrderStatus["Pending"] = "pending";
    TSalesOrderStatus["Preparing"] = "preparing";
    TSalesOrderStatus["Ready"] = "ready";
    TSalesOrderStatus["Delivering"] = "delivering";
    TSalesOrderStatus["Completed"] = "completed";
    TSalesOrderStatus["Refunded"] = "refunded";
    TSalesOrderStatus["Cancelled"] = "cancelled";
})(TSalesOrderStatus = exports.TSalesOrderStatus || (exports.TSalesOrderStatus = {}));
/**
 * A purchase of products a customer has made from a retail location
 * - Orders can either be picked up or delivered
 * - Orders can go through numerous stages before it is complete
 * - Orders can also be refunded if an issue occurs
 */
class SalesOrder {
    constructor() {
        this.printInfo = {
            status: printing_1.TSalesOrdersOrderPrintStatus.Ready
        };
    }
    get displayId() {
        const { salesOrderId } = this;
        return salesOrderId.substring(salesOrderId.length - 4);
    }
    get displayIdForStatus() {
        const { displayId, status } = this;
        // return status === TSalesOrderStatus.Pending
        //   ? "Waiting to be assigned..."
        //   : `#${displayId}`;
        return `#${displayId}`;
    }
    get displayIdHelpText() {
        return this.isDeliveryOrder
            ? "If you have any issues with your order use this number as a reference."
            : "Please have this number readily available on pickup to confirm your order. For security reasons, you may be asked to provide a valid ID along with the credit card used to purchase this order.";
    }
    get deliveryAddressText() {
        return this.deliveryAddress ? this.deliveryAddress.fullAddress : "";
    }
    get status() {
        if (this.cancelledAt) {
            return TSalesOrderStatus.Cancelled;
        }
        if (this.refundedAt) {
            return TSalesOrderStatus.Refunded;
        }
        if (this.completedAt) {
            return TSalesOrderStatus.Completed;
        }
        if (this.pickedUpByDeliveryDriverAt) {
            return TSalesOrderStatus.Delivering;
        }
        if (this.assembledAt) {
            return TSalesOrderStatus.Ready;
        }
        if (this.startedAssemblyAt) {
            return TSalesOrderStatus.Preparing;
        }
        if (this.createdAt) {
            return TSalesOrderStatus.Pending;
        }
        return TSalesOrderStatus.NotSubmitted;
    }
    get inProgress() {
        return (this.status !== TSalesOrderStatus.Pending &&
            this.status !== TSalesOrderStatus.Completed &&
            this.status !== TSalesOrderStatus.Refunded);
    }
    get outstanding() {
        return !this.completed;
    }
    get completed() {
        return (this.status === TSalesOrderStatus.Completed ||
            this.status === TSalesOrderStatus.Refunded);
    }
    get canOrderAtRetailLocation() {
        const { retailLocation, type } = this;
        return retailLocation.canOrderAtRetailLocation(type);
    }
    get isWithinDeliveryAddressDistance() {
        if (this.type === TSalesOrderType_1.TSalesOrderType.Pickup) {
            return true;
        }
        const { lat, lng } = this.deliveryAddress;
        return this.retailLocation.isWithinDeliveryDistance({ lat, lng });
    }
    /**
     * Check whether an order can be placed on the following factors:
     * - retail location must be open for placement time
     * - retail location must be available for order type
     * - if order type is delivery, then delivery address is within retail location's max distance
     */
    get canBePlaced() {
        return (this.retailLocation.isWithinOpeningHours(this.placementTime) &&
            this.canOrderAtRetailLocation &&
            this.isWithinDeliveryAddressDistance);
    }
    /**
     * Transforms sales order data such that it can be consumed by a comms
     * template
     */
    get commsData() {
        const { timezone: { timeZoneId } } = this.retailLocation;
        return Object.assign(Object.assign({}, this.serialize()), { type: labels_1.salesOrderTypeLabels[this.type], displayId: this.displayId, totalPrice: this.totalPrice.toFixed(2), salesOrderItems: this.salesOrderItems.map((salesOrderItem) => (Object.assign(Object.assign({}, salesOrderItem.serialize()), { title: salesOrderItem.title, description: salesOrderItem.type === TSalesOrderItemType_1.TSalesOrderItemType.Built
                    ? salesOrderItem.fullDescription
                    : "", price: (salesOrderItem.productId === "SERVICE"
                    ? salesOrderItem.totalPrice
                    : salesOrderItem.price).toFixed(2), cost: salesOrderItem.cost.toFixed(2) }))), promotions: Array.isArray(this.promotions)
                ? this.promotions.map((promotion) => ({
                    description: promotion.description,
                    priceDiscounted: promotion.priceDiscounted.toFixed(2)
                }))
                : undefined, hasPromotions: Array.isArray(this.promotions) && this.promotions.length > 0, 
            // map pick up time and delivery time to the same property for access
            // in the template.
            // the value that is mapped will be dependent on the type of the
            // order (delivery or pick up)
            time: moment
                .unix(this.predictedCompletionTime)
                .tz(timeZoneId)
                .format("h:mmA"), created: moment
                .unix(this.createdAt)
                .tz(timeZoneId)
                .format("Do MMMM YYYY"), isDoorDashDelivery: this.isDoorDashDelivery });
    }
    get salesOrderItemsGroupedByCategories() {
        const products = this.salesOrderItems.map(salesOrderItem => salesOrderItem.type === TSalesOrderItemType_1.TSalesOrderItemType.Fixed
            ? salesOrderItem.product
            : salesOrderItem.productBlueprint);
        const categories = (0, getUniqSortedCategories_1.getUniqSortedCategories)(products);
        return categories.map(category => ({
            category,
            salesOrderItems: this.salesOrderItems.filter(salesOrderItem => salesOrderItem.category &&
                salesOrderItem.category.name === category.name)
        }));
    }
    get hasPrintError() {
        var _a;
        return ((_a = this.printInfo) === null || _a === void 0 ? void 0 : _a.status) === printing_1.TSalesOrdersOrderPrintStatus.Failed;
    }
    get completedPrinting() {
        var _a;
        return ((_a = this.printInfo) === null || _a === void 0 ? void 0 : _a.status) === printing_1.TSalesOrdersOrderPrintStatus.Completed;
    }
    get isPrinting() {
        var _a;
        return [
            printing_1.TSalesOrdersOrderPrintStatus.Printing,
            printing_1.TSalesOrdersOrderPrintStatus.Reprint
        ].includes((_a = this.printInfo) === null || _a === void 0 ? void 0 : _a.status);
    }
    get canReprint() {
        var _a;
        return (this.hasPrintError ||
            this.completedPrinting ||
            ((_a = this.printInfo) === null || _a === void 0 ? void 0 : _a.status) === printing_1.TSalesOrdersOrderPrintStatus.Ignored);
    }
    /**
     * Determine whether this sales order can be re-ordered
     * The conditions must be:
     *
     *  - The sales order has been completed
     *  - The order type is available for the store it was ordered from
     *  - the store is operating today and the time now is before closing
     */
    get canReorder() {
        const { type, completed, retailLocation } = this;
        if (!completed) {
            return false;
        }
        const { canPickup, canDeliver } = retailLocation;
        if (this.type === TSalesOrderType_1.TSalesOrderType.Pickup && !canPickup) {
            return false;
        }
        if (type === TSalesOrderType_1.TSalesOrderType.Delivery && !canDeliver) {
            return false;
        }
        const now = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        const { isOpenToday, closingTime } = retailLocation.getOperatingDetailsForTime(now);
        if (!isOpenToday) {
            return false;
        }
        return now <= closingTime;
    }
    /**
     * Get a description of all the items ordered
     */
    get description() {
        return this.salesOrderItems
            .map((salesOrderItem) => {
            // for a build product we need to also factor in the main selection
            if (salesOrderItem.type === TSalesOrderItemType_1.TSalesOrderItemType.Built) {
                salesOrderItem = salesOrderItem;
                const mainSelection = salesOrderItem.mainSelection;
                const baseProduct = `${salesOrderItem.quantity} X ${salesOrderItem.productBlueprint.title}`;
                return mainSelection !== null
                    ? `${baseProduct} with ${mainSelection.title}`
                    : baseProduct;
            }
            // this is a stocked product so nothing extra to build
            return `${salesOrderItem.quantity} X ${salesOrderItem.product.title}`;
        })
            .join(", ");
    }
    /**
     * Get the main image to be displayed to identify this order
     */
    get mainItemImage() {
        const { salesOrderItems } = this;
        return (0, getOrderMainItemImage_1.getOrderMainItemImage)(salesOrderItems);
    }
    /**
     * Human friendly label to show when this was ordered
     *
     * Examples:
     *  - 33 minutes ago
     *  - 5 hours ago
     *  - 2 weeks ago
     */
    get orderedAtLabel() {
        if (this.placementTime === null) {
            return "";
        }
        const d1 = moment();
        const d2 = moment.unix(this.placementTime);
        const label = moment.duration(d1.diff(d2)).humanize();
        return `${label} ago`;
    }
    get completionTimeFormatted() {
        if (!this.completed) {
            return "";
        }
        return `${moment
            .unix(this.completedAt)
            .tz(this.retailLocation.timezone.timeZoneId)
            .format("h:mmA")}`;
    }
    get predictedCompletionTimeFormatted() {
        if (!this.predictedCompletionTime) {
            return "";
        }
        return `${moment
            .unix(this.predictedCompletionTime)
            .tz(this.retailLocation.timezone.timeZoneId)
            .format(constants_1.humanTimeFormat)}`;
    }
    /**
     * Expected time order is to be ready in format:
     *
     *  - 10:13am
     */
    get predictedCompletionTimeLabel() {
        if (!this.predictedCompletionTime) {
            return "";
        }
        return `Expected to be ready at ${moment
            .unix(this.predictedCompletionTime)
            .tz(this.retailLocation.timezone.timeZoneId)
            .format(constants_1.humanTimeFormat)}`;
    }
    /**
     * Total price discounted from promotions applied to this sales order
     */
    get promotionTotalPrice() {
        const { promotions } = this;
        // no promotions
        if (!Array.isArray(promotions) || promotions.length === 0) {
            return 0;
        }
        // total price is the addition of all discount prices of all promotions
        return promotions.reduce((total, current) => total - current.priceDiscounted, 0);
    }
    /**
     * Fallback function to calculate the total amount of a sales order.
     * Should normally rely on the totalPrice field but if for some reason this is not populated yet this can be used.
     *
     * The total amount will the sum of all ordered items (cost * quantity) subtracting all promotional prices applied.
     */
    get amount() {
        const { salesOrderItems, promotions, status, salesOrderRefund } = this;
        // total ordered items amount
        const totalItemAmount = salesOrderItems
            ? salesOrderItems.reduce((total, salesOrderItem) => parseFloat((total +
                salesOrderItem.quantity *
                    (salesOrderItem.productId === "SERVICE"
                        ? salesOrderItem.totalPrice
                        : salesOrderItem.price)).toFixed(2)), 0)
            : 0;
        // total promotional prices discounted
        const totalPromotionAmount = promotions
            ? promotions.reduce((total, promotion) => total + promotion.priceDiscounted, 0)
            : 0;
        const salesOrderAmount = totalItemAmount - totalPromotionAmount;
        // final sales order amount
        return status === TSalesOrderStatus.Refunded
            ? salesOrderAmount - salesOrderRefund.priceRefunded
            : salesOrderAmount;
    }
    /**
     * Order is overdue is its expected completion time has lapse past
     */
    get overDue() {
        const { predictedCompletionTime, completed } = this;
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        return predictedCompletionTime < timestampNow && !completed;
    }
    /**
     * Order is due now if its expected completion time is due between now and a certain period (usually 60 minutes)
     */
    get dueNow() {
        const { predictedCompletionTime, completed } = this;
        const duePeriod = parseInt(env.orderingDuePeriod) * 60 || 3600; // 60 minutes
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        return (predictedCompletionTime > timestampNow &&
            predictedCompletionTime < timestampNow + duePeriod &&
            !completed);
    }
    get completedDate() {
        const { completedAt, retailLocation: { timezone: { timeZoneId } } } = this;
        return `${(0, time_1.toTime)(completedAt, timeZoneId, constants_1.humanDateFormat)}`;
    }
    get refundedDate() {
        const { refundedAt, retailLocation: { timezone: { timeZoneId } } } = this;
        return `${(0, time_1.toTime)(refundedAt, timeZoneId, constants_1.humanDateFormat)}`;
    }
    /**
     * Order is due later if its expected completion time is after the due period (usually 60 minutes)
     */
    get dueLater() {
        const { predictedCompletionTime, completed } = this;
        const duePeriod = parseInt(env.orderingDuePeriod) * 60 || 3600; // 60 minutes
        const timestampNow = (0, currentUnixTimestamp_1.currentUnixTimestamp)();
        return predictedCompletionTime > timestampNow + duePeriod && !completed;
    }
    getCurrentStatusLabel(statusLabels) {
        return statusLabels[this.status];
    }
    get minimumDeliveryAmountFulfilled() {
        // TODO: move this to a helper function so it can shared with the hook version
        const { retailLocation: { minimumDeliveryAmount, deliveryCost }, totalPrice, salesOrderItems } = this;
        const serviceFeeItem = salesOrderItems.find(SO => SO.productId === "SERVICE");
        const serviceFee = serviceFeeItem ? serviceFeeItem.totalPrice : 0;
        if (!minimumDeliveryAmount) {
            return true;
        }
        return totalPrice - deliveryCost - serviceFee >= minimumDeliveryAmount;
    }
    get isValidDeliveryAddress() {
        const { deliveryAddress } = this;
        if (!deliveryAddress) {
            return false;
        }
        for (const addressProperty in deliveryAddress) {
            if (!deliveryAddress[addressProperty]) {
                return false;
            }
        }
        return true;
    }
    get totalItems() {
        return this.salesOrderItems.reduce((total, salesOrderItem) => (0, isDeliverySalesOrderItem_1.isDeliverySalesOrderItem)(salesOrderItem, env.deliveryId) ||
            (0, isServiceSalesOrderItem_1.isServiceSalesOrderItem)(salesOrderItem, env.serviceId)
            ? total
            : total + salesOrderItem.quantity, 0);
    }
    /**
     * Check if a store's location has DoorDash enabled and
     * that the sales order is a Delivery order.
     */
    get isDoorDashDelivery() {
        const { retailLocation: { deliveryType } } = this;
        return this.isDeliveryOrder && deliveryType === delivery_1.TDeliveryOptions.DoorDash;
    }
    /**
     * Check if delivery driver is with Car
     */
    get isDeliveryDriverWithCar() {
        var _a;
        const { deliveryDriver } = this;
        const dasherVehicleMake = (_a = deliveryDriver === null || deliveryDriver === void 0 ? void 0 : deliveryDriver.vehicle) === null || _a === void 0 ? void 0 : _a.brand;
        return !!dasherVehicleMake;
    }
    /**
     * Check if a store's location has Self Delivery enabled and
     * that the sales order is a Delivery order.
     */
    get isSelfDelivery() {
        const { retailLocation: { deliveryType } } = this;
        return this.isDeliveryOrder && deliveryType === delivery_1.TDeliveryOptions.Self;
    }
    get isAllowedToCancel() {
        // only if more than 40 minutes left to order due or pickup time allow restaurant to cancel it
        const addedMinutesToCurrentTime = 40;
        const { type, predictedCompletionTime, deliveryDriver, retailLocation: { deliveryType } } = this;
        const baseTimeToAllowToCancel = moment
            .utc()
            .add(addedMinutesToCurrentTime, "minutes");
        if (type === TSalesOrderType_1.TSalesOrderType.Pickup ||
            (type === TSalesOrderType_1.TSalesOrderType.Delivery &&
                deliveryType === delivery_1.TDeliveryOptions.Self)) {
            const predictedCompletionTimeInMoment = moment
                .unix(predictedCompletionTime)
                .utc();
            return predictedCompletionTimeInMoment.isAfter(baseTimeToAllowToCancel);
        }
        if (type === TSalesOrderType_1.TSalesOrderType.Delivery && (deliveryDriver === null || deliveryDriver === void 0 ? void 0 : deliveryDriver.pickupTime)) {
            const pickupTimeInMoment = moment.utc(deliveryDriver.pickupTime);
            return pickupTimeInMoment.isAfter(baseTimeToAllowToCancel);
        }
        return false;
    }
    get isPickupOrder() {
        return this.type === TSalesOrderType_1.TSalesOrderType.Pickup;
    }
    get isDeliveryOrder() {
        return this.type === TSalesOrderType_1.TSalesOrderType.Delivery;
    }
    filterSensitiveData() {
        const clone = (0, cloneModelObject_1.cloneModelObject)(this);
        delete clone.guid;
        delete clone.paymentToken;
        // TODO: move to zam specific
        delete clone.trtId;
        delete clone.trtTransactionId;
        delete clone.trtCreationStatus;
        delete clone.trtCreatedAt;
        delete clone.trtUpdatedAt;
        delete clone.trtFailureReason;
        return clone;
    }
    serialize() {
        return {
            salesOrderId: this.salesOrderId,
            guid: this.guid,
            retailLocation: this.retailLocation.serialize(),
            retailCustomer: this.retailCustomer
                ? this.retailCustomer.serialize()
                : null,
            salesOrderItems: this.salesOrderItems.map((salesOrderItem) => salesOrderItem.serialize()),
            type: this.type,
            deliveryAddress: this.deliveryAddress,
            temporaryAddress: this.temporaryAddress,
            notes: this.notes,
            paymentToken: this.paymentToken,
            oneTimePayment: this.oneTimePayment,
            createdAt: this.createdAt,
            updatedAt: this.updatedAt,
            placementTime: this.placementTime,
            requestedReadyTime: this.requestedReadyTime,
            predictedCompletionTime: this.predictedCompletionTime,
            startedAssemblyAt: this.startedAssemblyAt,
            assembledAt: this.assembledAt,
            pickedUpByDeliveryDriverAt: this.pickedUpByDeliveryDriverAt,
            completedAt: this.completedAt,
            refundedAt: this.refundedAt,
            cancelledAt: this.cancelledAt,
            printInfo: this.printInfo,
            salesOrderRefund: this.salesOrderRefund
                ? this.salesOrderRefund.serialize()
                : null,
            promotions: this.promotions,
            totalPrice: this.totalPrice,
            // TODO: move delivery/TRT to zam specific
            externalDeliveryReference: this.externalDeliveryReference,
            externalDeliveryTrackingUrl: this.externalDeliveryTrackingUrl,
            externalDeliveryStatus: this.externalDeliveryStatus,
            deliveryPartnerSupportPhoneNumber: this.deliveryPartnerSupportPhoneNumber,
            deliveryDriver: this.deliveryDriver,
            trtId: this.trtId,
            trtTransactionId: this.trtTransactionId,
            trtCreationStatus: this.trtCreationStatus,
            trtCreatedAt: this.trtCreatedAt,
            trtUpdatedAt: this.trtUpdatedAt,
            trtFailureReason: this.trtFailureReason
        };
    }
    static unserialize(data) {
        const newSalesOrder = new this();
        newSalesOrder.salesOrderId = data.salesOrderId;
        newSalesOrder.guid = data.guid;
        newSalesOrder.retailLocation = RetailLocation_1.RetailLocation.unserialize(data.retailLocation);
        newSalesOrder.retailCustomer = data.retailCustomer
            ? RetailCustomer_1.RetailCustomer.unserialize(data.retailCustomer)
            : null;
        newSalesOrder.salesOrderItems = data.salesOrderItems.map((salesOrderItem) => (0, salesOrderItemUnserializeFactory_1.salesOrderItemUnserializeFactory)(salesOrderItem));
        newSalesOrder.type = data.type;
        newSalesOrder.deliveryAddress = data.deliveryAddress;
        newSalesOrder.temporaryAddress = data.temporaryAddress;
        newSalesOrder.notes = data.notes;
        newSalesOrder.paymentToken = data.paymentToken;
        newSalesOrder.oneTimePayment = data.oneTimePayment;
        newSalesOrder.createdAt = data.createdAt;
        newSalesOrder.updatedAt = data.updatedAt;
        newSalesOrder.placementTime = data.placementTime;
        newSalesOrder.requestedReadyTime = data.requestedReadyTime;
        newSalesOrder.predictedCompletionTime = data.predictedCompletionTime;
        newSalesOrder.startedAssemblyAt = data.startedAssemblyAt;
        newSalesOrder.assembledAt = data.assembledAt;
        newSalesOrder.pickedUpByDeliveryDriverAt = data.pickedUpByDeliveryDriverAt;
        newSalesOrder.completedAt = data.completedAt;
        newSalesOrder.refundedAt = data.refundedAt;
        newSalesOrder.cancelledAt = data.cancelledAt;
        newSalesOrder.printInfo = data.printInfo;
        newSalesOrder.salesOrderRefund = data.salesOrderRefund
            ? SalesOrderRefund_1.SalesOrderRefund.unserialize(data.salesOrderRefund)
            : null;
        newSalesOrder.promotions = data.promotions;
        newSalesOrder.totalPrice = data.totalPrice;
        // TODO: move delivery/TRT to zam specific
        newSalesOrder.externalDeliveryReference = data.externalDeliveryReference;
        newSalesOrder.externalDeliveryTrackingUrl =
            data.externalDeliveryTrackingUrl;
        newSalesOrder.externalDeliveryStatus = data.externalDeliveryStatus;
        newSalesOrder.deliveryPartnerSupportPhoneNumber =
            data.deliveryPartnerSupportPhoneNumber;
        newSalesOrder.deliveryDriver = data.deliveryDriver;
        newSalesOrder.trtId = data.trtId;
        newSalesOrder.trtTransactionId = data.trtTransactionId;
        newSalesOrder.trtCreationStatus = data.trtCreationStatus;
        newSalesOrder.trtCreatedAt = data.trtCreatedAt;
        newSalesOrder.trtUpdatedAt = data.trtUpdatedAt;
        newSalesOrder.trtFailureReason = data.trtFailureReason;
        return newSalesOrder;
    }
    async validate() {
        const validated = await (0, class_validator_1.validate)(this);
        if (validated.length > 0) {
            throw new errorTypes_1.ValidationError(validated);
        }
        return true;
    }
}
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "salesOrderId", void 0);
__decorate([
    (0, class_validator_1.IsString)()
], SalesOrder.prototype, "guid", void 0);
__decorate([
    (0, class_validator_1.IsNotEmpty)(),
    (0, class_validator_1.ValidateNested)()
], SalesOrder.prototype, "retailLocation", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.ValidateNested)()
], SalesOrder.prototype, "retailCustomer", void 0);
__decorate([
    (0, class_validator_1.IsArray)(),
    (0, class_validator_1.ArrayNotEmpty)(),
    (0, class_validator_1.ValidateNested)()
], SalesOrder.prototype, "salesOrderItems", void 0);
__decorate([
    (0, class_validator_1.IsNotEmpty)(),
    (0, class_validator_1.IsIn)(Object.values(TSalesOrderType_1.TSalesOrderType))
], SalesOrder.prototype, "type", void 0);
__decorate([
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "deliveryType", void 0);
__decorate([
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "deliveryAddress", void 0);
__decorate([
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "temporaryAddress", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "notes", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "paymentToken", void 0);
__decorate([
    (0, class_validator_1.IsBoolean)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "oneTimePayment", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "createdAt", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "updatedAt", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)()
], SalesOrder.prototype, "placementTime", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)()
], SalesOrder.prototype, "requestedReadyTime", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "predictedCompletionTime", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "startedAssemblyAt", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "assembledAt", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "pickedUpByDeliveryDriverAt", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "completedAt", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "refundedAt", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "cancelledAt", void 0);
__decorate([
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "printInfo", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.ValidateNested)()
], SalesOrder.prototype, "salesOrderRefund", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.IsArray)()
], SalesOrder.prototype, "promotions", void 0);
__decorate([
    (0, class_validator_1.IsNumber)()
], SalesOrder.prototype, "totalPrice", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "externalDeliveryReference", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "externalDeliveryTrackingUrl", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "externalDeliveryStatus", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "deliveryPartnerSupportPhoneNumber", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "deliveryDriver", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "trtId", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "trtTransactionId", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "trtCreationStatus", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "trtCreatedAt", void 0);
__decorate([
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsPositive)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "trtUpdatedAt", void 0);
__decorate([
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)()
], SalesOrder.prototype, "trtFailureReason", void 0);
exports.SalesOrder = SalesOrder;
//# sourceMappingURL=SalesOrder.js.map